/**
 * Cuándo corre una programación.
 *
 * *** Toda hora que se muestra o se escribe acá es hora de Buenos Aires. *** El backend
 * guarda UTC y hace la conversión; el navegador nunca la hace, porque puede estar en
 * cualquier zona. Lo único que este componente calcula con la zona horaria es la fecha de
 * hoy para un valor por defecto, y eso sale de `Intl`, no de una resta de horas.
 */

import { Box, Card, Input, Label, Select, Text } from '@nimbus-ds/components';
import { useEffect, useState, type ReactElement } from 'react';
import type { Recurrencia } from '../api';

const ZONA = 'America/Argentina/Buenos_Aires';

/** `en-CA` formatea como AAAA-MM-DD, que es justo lo que come `<input type="date">`. */
const FECHA_EN_BUENOS_AIRES = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function hoyEnBuenosAires(): string {
  return FECHA_EN_BUENOS_AIRES.format(new Date());
}

export const RECURRENCIA_INICIAL: Recurrencia = { kind: 'monthly', day: 1, hour: 9, minute: 0 };

const DIAS: ReadonlyArray<{ valor: number; texto: string }> = [
  { valor: 1, texto: 'Lunes' },
  { valor: 2, texto: 'Martes' },
  { valor: 3, texto: 'Miércoles' },
  { valor: 4, texto: 'Jueves' },
  { valor: 5, texto: 'Viernes' },
  { valor: 6, texto: 'Sábado' },
  { valor: 0, texto: 'Domingo' },
];

interface Props {
  readonly valor: Recurrencia;
  readonly onValor: (valor: Recurrencia) => void;
}

export function CamposDeRecurrencia({ valor, onValor }: Props): ReactElement {
  return (
    <Card>
      <Card.Header title="Cuándo corre" />
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="4">
          <Select
            id="tipo-recurrencia"
            name="tipo-recurrencia"
            value={valor.kind}
            onChange={(evento) => onValor(recurrenciaPorDefecto(evento.currentTarget.value))}
          >
            <Select.Option value="monthly" label="Todos los meses" />
            <Select.Option value="weekly" label="Todas las semanas" />
            <Select.Option value="every_n_days" label="Cada tantos días" />
            <Select.Option value="once" label="Una sola vez" />
          </Select>

          {valor.kind === 'once' && (
            <Box display="flex" gap="2" flexWrap="wrap">
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="fecha">Fecha</Label>
                <Input
                  id="fecha"
                  name="fecha"
                  type="date"
                  value={valor.fechaLocal}
                  min={hoyEnBuenosAires()}
                  onChange={(evento) =>
                    onValor({ ...valor, fechaLocal: evento.currentTarget.value })
                  }
                />
              </Box>
              <EntradaDeHora
                hora={valor.horaLocal}
                onHora={(horaLocal) => onValor({ ...valor, horaLocal })}
              />
            </Box>
          )}

          {valor.kind === 'monthly' && (
            <Box display="flex" gap="2" flexWrap="wrap" alignItems="flex-end">
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="dia-del-mes">Día del mes</Label>
                <Select
                  id="dia-del-mes"
                  name="dia-del-mes"
                  value={String(valor.day)}
                  onChange={(evento) =>
                    onValor({ ...valor, day: Number(evento.currentTarget.value) })
                  }
                >
                  {Array.from({ length: 31 }, (_, indice) => indice + 1).map((dia) => (
                    <Select.Option key={dia} value={String(dia)} label={String(dia)} />
                  ))}
                </Select>
              </Box>
              <EntradaDeHora
                hora={comoTexto(valor.hour, valor.minute)}
                onHora={(texto) => onValor({ ...valor, ...deTexto(texto, valor) })}
              />
            </Box>
          )}

          {valor.kind === 'weekly' && (
            <Box display="flex" gap="2" flexWrap="wrap" alignItems="flex-end">
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="dia-de-semana">Día</Label>
                <Select
                  id="dia-de-semana"
                  name="dia-de-semana"
                  value={String(valor.weekday)}
                  onChange={(evento) =>
                    onValor({ ...valor, weekday: Number(evento.currentTarget.value) })
                  }
                >
                  {DIAS.map((dia) => (
                    <Select.Option key={dia.valor} value={String(dia.valor)} label={dia.texto} />
                  ))}
                </Select>
              </Box>
              <EntradaDeHora
                hora={comoTexto(valor.hour, valor.minute)}
                onHora={(texto) => onValor({ ...valor, ...deTexto(texto, valor) })}
              />
            </Box>
          )}

          {valor.kind === 'every_n_days' && (
            <Box display="flex" gap="2" flexWrap="wrap" alignItems="flex-end">
              <Box display="flex" flexDirection="column" gap="1">
                <Label htmlFor="cada-dias">Cada cuántos días</Label>
                <Input
                  id="cada-dias"
                  name="cada-dias"
                  type="number"
                  min="1"
                  max="365"
                  value={String(valor.days)}
                  onChange={(evento) =>
                    onValor({ ...valor, days: Number(evento.currentTarget.value) })
                  }
                />
              </Box>
              <EntradaDeHora
                hora={comoTexto(valor.hour, valor.minute)}
                onHora={(texto) => onValor({ ...valor, ...deTexto(texto, valor) })}
              />
            </Box>
          )}

          <Text fontSize="caption" color="neutral-textLow">
            Siempre en hora de Buenos Aires.
            {valor.kind === 'monthly' && valor.day > 28
              ? ` Si el mes no llega al día ${valor.day}, corre el último día del mes.`
              : ''}
          </Text>
        </Box>
      </Card.Body>
    </Card>
  );
}

/**
 * `<input type="time">` se puede vaciar mientras se tipea, y el modelo no acepta vacío.
 * Igual que en `EntradaDePesos`, el texto vive acá y el valor externo sólo lo pisa cuando
 * dejó de representarlo: si no, borrar un dígito devolvería el valor viejo de un salto.
 */
function EntradaDeHora({
  hora,
  onHora,
}: {
  readonly hora: string;
  readonly onHora: (valor: string) => void;
}): ReactElement {
  const [texto, setTexto] = useState(hora);

  useEffect(() => {
    setTexto((actual) => (actual === hora ? actual : hora));
  }, [hora]);

  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Label htmlFor="hora">Hora</Label>
      <Input
        id="hora"
        name="hora"
        type="time"
        value={texto}
        onChange={(evento) => {
          const nuevo = evento.currentTarget.value;
          setTexto(nuevo);
          if (/^\d{2}:\d{2}$/.test(nuevo)) onHora(nuevo);
        }}
      />
    </Box>
  );
}

function comoTexto(hour: number, minute: number): string {
  const dd = (n: number): string => String(n).padStart(2, '0');
  return `${dd(hour)}:${dd(minute)}`;
}

function deTexto(texto: string, actual: { hour: number; minute: number }): {
  hour: number;
  minute: number;
} {
  const [hora, minuto] = texto.split(':').map(Number);
  if (!Number.isFinite(hora) || !Number.isFinite(minuto)) return actual;
  return { hour: hora as number, minute: minuto as number };
}

function recurrenciaPorDefecto(kind: string): Recurrencia {
  switch (kind) {
    case 'weekly':
      return { kind: 'weekly', weekday: 1, hour: 9, minute: 0 };
    case 'every_n_days':
      return { kind: 'every_n_days', days: 7, hour: 9, minute: 0 };
    case 'once':
      return { kind: 'once', fechaLocal: hoyEnBuenosAires(), horaLocal: '09:00' };
    default:
      return RECURRENCIA_INICIAL;
  }
}
